import React, { useMemo, useState } from "react";
import {
    Container,
    Paper,
    Stack,
    Group,
    Title,
    Text,
    TextInput,
    Button,
    Badge,
    Table,
    Loader,
    Center,
    Divider,
    ActionIcon,
    Tooltip,
    ScrollArea,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
    IconPlaneArrival,
    IconPlaneDeparture,
    IconCircleCheck,
    IconClock,
    IconX,
    IconCircle,
    IconMail,
    IconBrandWhatsapp,
    IconSearch,
    IconEdit,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";

import {
    PublicTransportBookingReadDto,
} from "../../types/transportPublicTypes";
import { getMyTransportBookings } from "../../api/transportPublicApi";
import EditMyBookingDrawer from "./EditMyBookingDrawer";

const RED = "#ff5e5e";

function kindToString(kind: number | null | undefined): string {
    switch (kind) {
        case 1:
            return "arrival";
        case 2:
            return "departure";
        case 3:
            return "local";
        default:
            return "";
    }
}

function kindMeta(kind: number | string | null | undefined) {
    const v = typeof kind === "number" ? kindToString(kind) : String(kind ?? "").toLowerCase();

    switch (v) {
        case "arrival":
            return { label: "Arrival", icon: <IconPlaneArrival size={16} /> };
        case "departure":
            return { label: "Departure", icon: <IconPlaneDeparture size={16} /> };
        case "local":
            return { label: "Local", icon: <span style={{ fontSize: 16 }}>🚗</span> };
        default:
            return { label: "Unknown", icon: <span style={{ fontSize: 16 }}>❓</span> };
    }
}

function isArrival(kind: any) {
    return kind === 1 || String(kind).toLowerCase() === "arrival";
}

function isDeparture(kind: any) {
    return kind === 2 || String(kind).toLowerCase() === "departure";
}

function fromToForRow(b: PublicTransportBookingReadDto) {
    const pickup = b.pickUpPlace ?? "-";
    const dropoff = b.dropOffPlace ?? "-";
    const flight = b.flightNo ? b.flightNo : "Flight";

    if (isArrival(b.kind)) return { fromText: flight, toText: dropoff };
    if (isDeparture(b.kind)) return { fromText: pickup, toText: flight };
    return { fromText: pickup, toText: dropoff };
}

function statusMetaFromString(status?: string | null) {
    const s = String(status ?? "").toLowerCase().trim();

    if (["confirmed", "confirm", "ok"].includes(s))
        return { label: "Confirmed", color: "green" as const, icon: <IconCircleCheck size={14} /> };

    if (["delayed", "delay"].includes(s))
        return { label: "Delayed", color: "orange" as const, icon: <IconClock size={14} /> };

    if (["cancelled", "canceled", "cancel"].includes(s))
        return { label: "Cancelled", color: "red" as const, icon: <IconX size={14} /> };

    if (s) return { label: status as string, color: "gray" as const, icon: <IconCircle size={14} /> };

    return { label: "Booked", color: "gray" as const, icon: <IconCircle size={14} /> };
}

export default function TransportMyBookings() {
    const { eventId } = useParams<{ eventId: string }>();
    const eventIdNum = eventId ? Number(eventId) : NaN;

    const [email, setEmail] = useState("");
    const [whatsAppNumber, setWhatsAppNumber] = useState("");

    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState<PublicTransportBookingReadDto[]>([]);

    const [searchEmail, setSearchEmail] = useState("");
    const [searchWhatsAppNumber, setSearchWhatsAppNumber] = useState("");

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selected, setSelected] = useState<PublicTransportBookingReadDto | null>(null);

    const totalBookings = useMemo(() => bookings.length, [bookings]);
    const totalPassengers = useMemo(
        () => bookings.reduce((sum, b) => sum + (b.numberOfPersons ?? 0), 0),
        [bookings]
    );

    const fetchBookings = async (emailValue?: string, whatsAppValue?: string) => {
        const emailToUse = (emailValue ?? searchEmail).trim();
        const whatsAppToUse = (whatsAppValue ?? searchWhatsAppNumber).trim();

        if (!eventId || Number.isNaN(eventIdNum)) {
            showNotification({
                color: "red",
                title: "Mangler eventId",
                message: "URL skal indeholde /:eventId",
            });
            return;
        }

        if (!emailToUse || !whatsAppToUse) {
            showNotification({
                color: "red",
                title: "Manglende oplysninger",
                message: "Du skal udfylde både email og WhatsApp nummer.",
            });
            return;
        }

        setLoading(true);

        try {
            const data = await getMyTransportBookings(eventIdNum, {
                email: emailToUse,
                whatsAppNumber: whatsAppToUse,
            });

            setBookings(data);
            setSearchEmail(emailToUse);
            setSearchWhatsAppNumber(whatsAppToUse);
        } catch (e: any) {
            showNotification({
                color: "red",
                title: "Fejl",
                message: e?.response?.data || "Kunne ikke hente bookings.",
                autoClose: 8000,
            });
        } finally {
            setLoading(false);
        }
    };

    const search = async () => {
        await fetchBookings(email, whatsAppNumber);
    };

    const openEdit = (row: PublicTransportBookingReadDto) => {
        setSelected(row);
        setDrawerOpen(true);
    };

    const CELL_BASE: React.CSSProperties = {
        borderTop: "1px solid rgba(0,0,0,0.06)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        background: "white",
    };

    function statusMetaFromId(id?: number | null) {
        switch (id) {
            case 3:
                return { label: "Confirmed", color: "green" as const, icon: <IconCircleCheck size={14} /> };
            case 2:
                return { label: "Delayed", color: "orange" as const, icon: <IconClock size={14} /> };
            case 4:
                return { label: "Cancelled", color: "red" as const, icon: <IconX size={14} /> };
            default:
                return { label: "Booked", color: "gray" as const, icon: <IconCircle size={14} /> };
        }
    }

    return (
        <Container size="xl" py="xl">
            <Stack gap="md">
                <Paper withBorder radius="md" p="lg">
                    <Stack gap="md">
                        <Stack gap={2}>
                            <Title order={3}>My transport bookings</Title>
                            <Text c="dimmed" size="sm">
                                Find and edit your existing booking
                            </Text>
                        </Stack>

                        <Divider />

                        <Group align="flex-end" wrap="wrap" gap="sm">
                            <TextInput
                                label="Email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.currentTarget.value)}
                                leftSection={<IconMail size={16} />}
                                style={{ minWidth: 260 }}
                            />

                            <TextInput
                                label="WhatsApp number"
                                placeholder="Without country code"
                                value={whatsAppNumber}
                                onChange={(e) => setWhatsAppNumber(e.currentTarget.value.replace(/\D/g, ""))}
                                leftSection={<IconBrandWhatsapp size={16} />}
                                style={{ minWidth: 240 }}
                            />

                            <Button
                                onClick={search}
                                leftSection={<IconSearch size={16} />}
                                loading={loading}
                                style={{ backgroundColor: RED }}
                                styles={{
                                    root: {
                                        transition: "transform 120ms ease, box-shadow 120ms ease",
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                            backgroundColor: RED,
                                        },
                                    },
                                }}
                            >
                                Find bookings
                            </Button>

                            <Group ml="auto" gap="xs">
                                <Badge variant="light" color="red">{totalBookings} bookings</Badge>
                                <Badge variant="light" color="red">{totalPassengers} pax</Badge>
                            </Group>
                        </Group>
                    </Stack>
                </Paper>

                <Paper withBorder radius="md" p="lg">
                    <Group justify="space-between" mb="sm">
                        <Title order={4}>Bookings</Title>
                        {loading && <Loader size="sm" />}
                    </Group>

                    {loading ? (
                        <Center py="xl"><Loader /></Center>
                    ) : bookings.length === 0 ? (
                        <Text c="dimmed">Ingen bookings endnu. Søg med email og WhatsApp nummer.</Text>
                    ) : (
                        <ScrollArea>
                            <Table
                                highlightOnHover
                                verticalSpacing="sm"
                                horizontalSpacing="md"
                                withRowBorders={false}
                                style={{
                                    borderCollapse: "separate",
                                    borderSpacing: "0 10px",
                                }}
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Kind</Table.Th>
                                        <Table.Th>Date</Table.Th>
                                        <Table.Th>Time</Table.Th>
                                        <Table.Th>From → To</Table.Th>
                                        <Table.Th>Contact</Table.Th>
                                        <Table.Th>Status</Table.Th>
                                        <Table.Th>Pax</Table.Th>
                                        <Table.Th style={{ width: 60, textAlign: "right" }} />
                                    </Table.Tr>
                                </Table.Thead>

                                <Table.Tbody>
                                    {bookings.map((b) => {
                                        const km = kindMeta(b.kind);
                                        const sm = statusMetaFromId(b.statusTransportId);
                                        const ft = fromToForRow(b);

                                        const firstCellStyle: React.CSSProperties = {
                                            ...CELL_BASE,
                                            borderLeft: "1px solid rgba(0,0,0,0.06)",
                                            borderTopLeftRadius: 10,
                                            borderBottomLeftRadius: 10,
                                            paddingLeft: 14,
                                        };

                                        const middleCellStyle: React.CSSProperties = {
                                            ...CELL_BASE,
                                        };

                                        const lastCellStyle: React.CSSProperties = {
                                            ...CELL_BASE,
                                            borderRight: "1px solid rgba(0,0,0,0.06)",
                                            borderTopRightRadius: 10,
                                            borderBottomRightRadius: 10,
                                            textAlign: "right",
                                            paddingRight: 10,
                                        };

                                        return (
                                            <Table.Tr key={b.transportRequestId}>
                                                <Table.Td style={firstCellStyle}>
                                                    <Group gap={6} wrap="nowrap">
                                                        {km.icon}
                                                        <Text size="sm" fw={700}>{km.label}</Text>
                                                    </Group>
                                                    {b.flightNo ? <Text size="xs" c="dimmed">Flight: {b.flightNo}</Text> : null}
                                                </Table.Td>

                                                <Table.Td style={middleCellStyle}>
                                                    <Text size="sm">{b.scheduledDate ? b.scheduledDate.slice(0, 10) : "-"}</Text>
                                                </Table.Td>

                                                <Table.Td style={middleCellStyle}>
                                                    <Text size="sm">{b.scheduledTime ?? "-"}</Text>
                                                </Table.Td>

                                                <Table.Td style={middleCellStyle}>
                                                    <Text size="sm" fw={700}>{ft.fromText}</Text>
                                                    <Text size="xs" c="dimmed">→ {ft.toText}</Text>
                                                </Table.Td>

                                                <Table.Td style={middleCellStyle}>
                                                    <Text size="sm" fw={700}>{b.contactPerson ?? "-"}</Text>
                                                    <Text size="xs" c="dimmed">
                                                        {b.contactPersonEmail ?? "-"}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td style={middleCellStyle}>
                                                    <Badge variant="light" color={sm.color} leftSection={sm.icon}>
                                                        {sm.label}
                                                    </Badge>
                                                </Table.Td>

                                                <Table.Td style={middleCellStyle}>
                                                    <Badge variant="light" color="red">
                                                        {b.numberOfPersons ?? 0}
                                                    </Badge>
                                                </Table.Td>

                                                <Table.Td style={lastCellStyle}>
                                                    <Tooltip label="Rediger booking">
                                                        <ActionIcon
                                                            variant="light"
                                                            onClick={() => openEdit(b)}
                                                            style={{ color: RED }}
                                                        >
                                                            <IconEdit size={18} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}
                </Paper>
            </Stack>

            <EditMyBookingDrawer
                opened={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                booking={selected}
                eventId={eventIdNum}
                email={searchEmail}
                whatsAppNumber={searchWhatsAppNumber}
                onSaved={() => fetchBookings()}
            />
        </Container>
    );
}